import { request } from "../../../utils";
import {
  CryptoWallet,
  CryptoToken,
  CryptoNetworkCode,
  CryptoTxType,
  CryptoTransaction,
  CryptoTransactionListResponse,
} from "../types";

const toNumber = (v: unknown) => (v === undefined || v === null ? 0 : parseFloat(String(v)));

const mapTransaction = (tx: any): CryptoTransaction => ({
  id: tx.id,
  walletId: tx.walletId,
  txHash: tx.txHash ?? null,
  token: tx.token,
  network: tx.network,
  amount: toNumber(tx.amount),
  fee: toNumber(tx.fee),
  type: tx.type,
  status: tx.status,
  from: tx.from,
  to: tx.to,
  blockNumber: tx.blockNumber ?? null,
  createdAt: tx.createdAt,
  updatedAt: tx.updatedAt,
});

/* ---------------- Wallet ---------------- */

// There is only ever one Stable coin Wallet, holding multiple tokens across multiple networks
export const fetchCryptoWallet = async (): Promise<CryptoWallet | null> => {
  const res = await request.get({ url: "/stable-currency/balance" });
  const data = res.data;
  if (!data) return null;

  const totalBalance = Object.fromEntries(
    Object.entries(data.totalBalance ?? {}).map(([token, value]) => [token, toNumber(value)])
  ) as Record<CryptoToken, number>;

  return {
    address: data.address,
    tronAddress: data.tronAddress,
    solanaAddress: data.solanaAddress,
    totalBalance,
    balances: (data.balances ?? []).map((b: any) => ({
      network: b.network,
      token: b.token,
      balance: toNumber(b.balance),
      feeBalance: toNumber(b.feeBalance),
      available: !!b.available,
    })),
  };
};

// POST with no payload
export const createCryptoWallet = async () => {
  const res = await request.post({ url: "/stable-currency/wallet", data: {} });
  return res.data;
};

export const withdrawCrypto = async (fields: {
  token: CryptoToken;
  network: string;
  toAddress: string;
  amount: string;
  memo?: string;
  narration?: string;
}) => {
  const idempotencyKey = `${fields.token}-${fields.network}-${fields.toAddress}-${fields.amount}-${Date.now()}`;

  try {
    const res = await request.post({
      url: "/stable-currency/withdraw",
      headers: { idempotencykey: idempotencyKey },
      data: fields,
    });

    if (res?.data && res.data.success === false) {
      const err: any = new Error(res.data.message || "Withdrawal failed");
      err.response = { data: res.data };
      throw err;
    }

    return res.data;
  } catch (err: any) {
    if (!err?.response?.data?.message) {
      const normalized: any = new Error(err?.message || "Withdrawal failed");
      normalized.response = { data: { message: err?.message || "Withdrawal failed", ...err } };
      throw normalized;
    }
    throw err;
  }
};

/* ---------------- Transactions ---------------- */

// Query params mirror the backend's Joi validation for this route exactly:
// page, limit, tokenType, network, type - all optional. No orderBy/order.
export const listCryptoTransactions = async (filters: {
  page?: number;
  limit?: number;
  tokenType?: CryptoToken;
  network?: CryptoNetworkCode;
  type?: CryptoTxType;
}): Promise<CryptoTransactionListResponse> => {
  const query = new URLSearchParams();
  if (filters.page) query.set("page", String(filters.page));
  if (filters.limit) query.set("limit", String(filters.limit));
  if (filters.tokenType) query.set("tokenType", filters.tokenType);
  if (filters.network) query.set("network", filters.network);
  if (filters.type) query.set("type", filters.type);

  const res = await request.get({ url: `/stable-currency/transactions?${query.toString()}` });
  const data = res.data;

  return {
    data: (data?.data ?? []).map(mapTransaction),
    pageCount: data?.pageCount ?? 1,
    pageId: data?.pageId ?? 1,
    limit: data?.limit ?? 10,
    totalCount: data?.totalCount ?? 0,
  };
};

export const fetchCryptoTransaction = async (
  transactionId: string
): Promise<CryptoTransaction> => {
  const res = await request.get({ url: `/stable-currency/transaction/${transactionId}` });
  return mapTransaction(res.data);
};

export const fetchCryptoWithdrawalFee = async (
  token: string,
  amount: string
): Promise<{ fee: number; minPayout: number | null }> => {
  try {
    const res = await request.get({
      url: `/stable-currency/withdrawal-fee?tokenType=${token}&amount=${amount}`,
    });
    return {
      fee: Number(res?.data?.fee) || 0,
      minPayout: res?.data?.minPayout != null ? Number(res.data.minPayout) : null,
    };
  } catch (error) {
    console.error(`Error fetching withdrawal fee for ${token}:`, error);
    return { fee: 0, minPayout: null };
  }
};